import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  BuildingLibraryIcon,
  HomeIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { API_URLS } from '../../config/api';
import axiosInstance from '../../utils/axios';

function BookRide() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState(null); // 'to-church' or 'from-church'
  const [formData, setFormData] = useState({
    // Address fields
    street: '',
    city: '',
    postcode: '',
    // Booking details
    date: '',
    service: '1st',
    passengers: 1,
    specialRequests: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Default church address
  const churchAddress = "Unit 2, 3740 27 St NE, Calgary, Alberta T1Y 5E2";

  // Service times
  const services = [
    { id: '1st', name: '1st Service', time: '8:00 AM' },
    { id: '2nd', name: '2nd Service', time: '10:00 AM' },
    { id: '3rd', name: '3rd Service', time: '12:00 PM' },
    { id: 'midweek', name: 'Midweek', time: '6:00 PM' },
  ];

  const rideTypes = [
    {
      id: 'to-church',
      title: 'To Church',
      description: 'Need a ride to church? We\'ll pick you up and take you to the service.',
      icon: BuildingLibraryIcon,
      colorClasses: {
        bg: 'bg-indigo-600',
        bgHover: 'hover:bg-indigo-700',
        bgLight: 'bg-indigo-100',
        bgLightHover: 'group-hover:bg-indigo-200',
        text: 'text-indigo-600',
        textHover: 'group-hover:text-indigo-500',
        textActive: 'group-hover:text-indigo-700',
        border: 'hover:border-indigo-300',
        ring: 'focus:ring-indigo-500'
      },
      addressLabel: 'Your Pickup Address',
      addressPlaceholder: 'Where should we pick you up?'
    },
    {
      id: 'from-church',
      title: 'From Church',
      description: 'Need a ride home after church? We\'ll take you home from the service.',
      icon: HomeIcon,
      colorClasses: {
        bg: 'bg-green-600',
        bgHover: 'hover:bg-green-700',
        bgLight: 'bg-green-100',
        bgLightHover: 'group-hover:bg-green-200',
        text: 'text-green-600',
        textHover: 'group-hover:text-green-500',
        textActive: 'group-hover:text-green-700',
        border: 'hover:border-green-300',
        ring: 'focus:ring-green-500'
      },
      addressLabel: 'Your Home Address',
      addressPlaceholder: 'Where should we drop you off?'
    }
  ];

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setError('');
  };

  const handleBack = () => {
    setSelectedType(null);
    setFormData({
      street: '',
      city: '',
      postcode: '',
      date: '',
      service: '1st',
      passengers: 1,
      specialRequests: ''
    });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const selectedService = services.find(s => s.id === formData.service);
      const fullAddress = `${formData.street}, ${formData.city} ${formData.postcode}`;
      
      let bookingData = {
        date: formData.date,
        serviceSession: formData.service,
        serviceTime: selectedService.time,
        passengers: parseInt(formData.passengers),
        seats: parseInt(formData.passengers),
        specialRequests: formData.specialRequests.trim() || undefined
      };

      if (selectedType === 'to-church') {
        // To church: user address is pickup, church is destination
        bookingData = {
          ...bookingData,
          pickupStreet: formData.street,
          pickupCity: formData.city,
          pickupPostcode: formData.postcode,
          pickupLocation: fullAddress,
          destination: churchAddress
        };
      } else {
        // From church: church is pickup, user address is destination
        bookingData = {
          ...bookingData,
          pickupStreet: "Unit 2, 3740 27 St NE",
          pickupCity: "Calgary",
          pickupPostcode: "Alberta T1Y 5E2",
          pickupLocation: churchAddress,
          destination: fullAddress,
          // For from-church rides, we also store the destination details
          destinationStreet: formData.street,
          destinationCity: formData.city,
          destinationPostcode: formData.postcode
        };
      }

      console.log('Sending booking data:', bookingData);
      console.log('Special requests value:', formData.specialRequests);
      console.log('Special requests in bookingData:', bookingData.specialRequests);

      const response = await axiosInstance.post(API_URLS.bookings.create, bookingData);
      navigate('/rider/my-bookings');
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.response?.data?.message || 'Failed to book ride. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedRideType = rideTypes.find(type => type.id === selectedType);

  // Step 1: Ride Type Selection
  if (!selectedType) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Book a Ride</h1>
          <p className="text-lg text-gray-600">Choose your ride type to get started</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {rideTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <div
                key={type.id}
                onClick={() => handleTypeSelect(type.id)}
                className={`relative cursor-pointer rounded-lg border-2 border-gray-200 p-6 shadow-sm hover:shadow-md transition-all duration-200 ${type.colorClasses.border} group`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${type.colorClasses.bgLight} ${type.colorClasses.bgLightHover} transition-colors`}>
                    <IconComponent className={`w-6 h-6 ${type.colorClasses.text}`} />
                  </div>
                  <ArrowRightIcon className={`w-5 h-5 text-gray-400 ${type.colorClasses.textHover} transition-colors`} />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600">{type.description}</p>
                
                <div className={`mt-4 inline-flex items-center ${type.colorClasses.text} font-medium ${type.colorClasses.textActive}`}>
                  Select this option
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Step 2: Booking Form
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Header */}
        <div className={selectedType === 'to-church' ? 'bg-indigo-600 px-6 py-4' : 'bg-green-600 px-6 py-4'}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <selectedRideType.icon className="w-6 h-6 text-white mr-3" />
              <h1 className="text-xl font-bold text-white">{selectedRideType.title}</h1>
            </div>
            <button
              onClick={handleBack}
              className="text-white hover:text-gray-200 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Route Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-sm font-medium text-gray-700 mb-3">Route</h2>
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  {selectedType === 'to-church' ? 'Pickup' : 'Pickup (Church)'}
                </div>
                <div className="text-sm text-gray-900">
                  {selectedType === 'to-church' ? 'Your Address' : churchAddress}
                </div>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400" />
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-500 mb-1">
                  {selectedType === 'to-church' ? 'Destination (Church)' : 'Destination'}
                </div>
                <div className="text-sm text-gray-900">
                  {selectedType === 'to-church' ? churchAddress : 'Your Address'}
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Address Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedRideType.addressLabel}
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="street" className="block text-sm font-medium text-gray-700">
                    Street Address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="123 Main Street"
                      value={formData.street}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Toronto"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="postcode" className="block text-sm font-medium text-gray-700">
                      Postcode
                    </label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="M5V 3A8"
                      value={formData.postcode}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                  Date
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    value={formData.date}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700">
                  Service
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  className="mt-1 block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.service}
                  onChange={handleChange}
                >
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {service.time}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="passengers" className="block text-sm font-medium text-gray-700">
                Number of Passengers
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="passengers"
                  name="passengers"
                  min="1"
                  max="4"
                  required
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  value={formData.passengers}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Special Requests */}
            <div>
              <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700">
                Special Requests (Optional)
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  rows="3"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Any special requests or notes for the driver? (e.g., wheelchair accessible, child seat needed, etc.)"
                  value={formData.specialRequests}
                  onChange={handleChange}
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Let the driver know about any special requirements or preferences you may have.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className={selectedType === 'to-church' 
                  ? "inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  : "inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                }
              >
                {loading ? 'Booking...' : `Book ${selectedRideType.title}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BookRide;
